/**
 * Inkscape-style SVG path simplification for oversized catalog / user-model
 * footprints. Shrinks dense M/L hatch geometry so plan JSON stays under the
 * localStorage and network budgets (see Lionbar ~7 MB plans).
 *
 * Only absolute M / L / Z path data is rewritten. Relative or curve commands
 * leave the path untouched. Library webpack URLs are never rewritten.
 */

export const SVG_SIMPLIFY_MAX_BYTES = 10_240;

/** Tolerances calibrated against the exported Gubi Beetle footprint. */
const SIMPLIFY_TOLERANCE = 0.4;
const MIN_SEGMENT_LENGTH = 0.5;
const CHAIN_EPSILON = 0.2;
const COORD_DECIMALS = 1;
/** Soften RDP on short closed outlines so silhouettes stay clean. */
const OUTLINE_TOLERANCE_FACTOR = 0.35;
const OUTLINE_MIN_SEG_FACTOR = 0.25;
const OUTLINE_D_CHAR_LIMIT = 1000;
/**
 * Reject a simplified SVG if its path bounding box drifts more than this
 * fraction of the original span (width or height). Unknown footprints that
 * would visually shift are left alone instead of being written back.
 */
const BBOX_MAX_RELATIVE_DRIFT = 0.02;

type Point = [number, number];

/** Session memo: original svgPath / markup → simplified result. */
const simplifyMemo = new Map<string, string>();

/** Test helper — clears the session memo between cases. */
export const clearSimplifySvgMemo = (): void => {
  simplifyMemo.clear();
};

const byteLength = (value: string): number => {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(value).length;
  }
  // Node / Jest fallback without TextEncoder polyfill.
  return Buffer.byteLength(value, 'utf8');
};

const dist2 = (a: Point, b: Point): number => {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  return dx * dx + dy * dy;
};

const segmentLength = (a: Point, b: Point): number =>
  Math.hypot(b[0] - a[0], b[1] - a[1]);

const polylineLength = (points: Point[]): number => {
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    total += segmentLength(points[i - 1], points[i]);
  }
  return total;
};

const perpendicularDistance = (p: Point, a: Point, b: Point): number => {
  const [ax, ay] = a;
  const [bx, by] = b;
  const [px, py] = p;
  const dx = bx - ax;
  const dy = by - ay;
  if (dx === 0 && dy === 0) {
    return Math.hypot(px - ax, py - ay);
  }
  let t = ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy);
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
};

/**
 * Ramer–Douglas–Peucker. Preserves closed rings when the first and last
 * points coincide.
 */
const rdp = (points: Point[], epsilon: number): Point[] => {
  if (points.length < 3) {
    return points;
  }
  const closed =
    points.length > 2 &&
    points[0][0] === points[points.length - 1][0] &&
    points[0][1] === points[points.length - 1][1];
  const open = closed ? points.slice(0, -1) : points;

  const simplify = (pts: Point[]): Point[] => {
    if (pts.length < 3) {
      return pts;
    }
    const a = pts[0];
    const b = pts[pts.length - 1];
    let maxDistance = 0;
    let index = 0;
    for (let i = 1; i < pts.length - 1; i += 1) {
      const distance = perpendicularDistance(pts[i], a, b);
      if (distance > maxDistance) {
        maxDistance = distance;
        index = i;
      }
    }
    if (maxDistance > epsilon) {
      const left = simplify(pts.slice(0, index + 1));
      const right = simplify(pts.slice(index));
      return left.slice(0, -1).concat(right);
    }
    return [pts[0], pts[pts.length - 1]];
  };

  const result = simplify(open);
  if (
    closed &&
    (result[0][0] !== result[result.length - 1][0] ||
      result[0][1] !== result[result.length - 1][1])
  ) {
    result.push([result[0][0], result[0][1]]);
  }
  return result;
};

/**
 * Parse absolute M / L / Z path data into subpaths.
 * Returns null when relative or curve commands are present so callers leave
 * the original d attribute alone.
 */
const parseAbsolutePathD = (d: string): Point[][] | null => {
  const tokens = d.match(
    /[MmLlZzHhVvCcSsQqTtAa]|[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g,
  );
  if (!tokens || tokens.length === 0) {
    return [];
  }

  const subpaths: Point[][] = [];
  let current: Point[] = [];
  let command: string | null = null;
  let i = 0;

  while (i < tokens.length) {
    const token = tokens[i];
    if (/^[MmLlZzHhVvCcSsQqTtAa]$/.test(token)) {
      // Relative or curve commands are unsupported in v1.
      if (/^[mlhvcsqta]$/.test(token) || /^[HhVvCcSsQqTtAa]$/.test(token)) {
        return null;
      }
      command = token;
      i += 1;
      if (command === 'Z' || command === 'z') {
        if (current.length > 0) {
          if (
            current.length > 1 &&
            (current[0][0] !== current[current.length - 1][0] ||
              current[0][1] !== current[current.length - 1][1])
          ) {
            current.push([current[0][0], current[0][1]]);
          }
          subpaths.push(current);
          current = [];
        }
      }
      continue;
    }

    if (command === null || (command !== 'M' && command !== 'L')) {
      return null;
    }

    if (i + 1 >= tokens.length) {
      return null;
    }
    const x = Number(tokens[i]);
    const y = Number(tokens[i + 1]);
    i += 2;
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return null;
    }

    if (command === 'M') {
      if (current.length > 0) {
        subpaths.push(current);
      }
      current = [[x, y]];
      // Subsequent coordinate pairs after M are implicit L.
      command = 'L';
    } else {
      current.push([x, y]);
    }
  }

  if (current.length > 0) {
    subpaths.push(current);
  }
  return subpaths;
};

const formatCoord = (value: number): string => {
  const fixed = value.toFixed(COORD_DECIMALS);
  if (COORD_DECIMALS > 0) {
    return fixed.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1') || '0';
  }
  return fixed;
};

const pathToD = (subpaths: Point[][]): string => {
  const parts: string[] = [];
  for (const points of subpaths) {
    if (points.length < 2) {
      continue;
    }
    const closed =
      points[0][0] === points[points.length - 1][0] &&
      points[0][1] === points[points.length - 1][1];
    const sequence =
      closed && points.length > 2 ? points.slice(0, -1) : points;
    if (sequence.length < 1) {
      continue;
    }
    let d = `M${formatCoord(sequence[0][0])} ${formatCoord(sequence[0][1])}`;
    for (let i = 1; i < sequence.length; i += 1) {
      d += `L${formatCoord(sequence[i][0])} ${formatCoord(sequence[i][1])}`;
    }
    if (closed) {
      d += 'Z';
    }
    parts.push(d);
  }
  return parts.join('');
};

/**
 * Greedily chain open polylines whose endpoints nearly touch, using spatial
 * buckets so Lionbar-scale (~2400) segment counts stay cheap.
 */
const chainSegments = (subpaths: Point[][], epsilon: number): Point[][] => {
  const opens = subpaths.filter(
    (sp) =>
      sp.length >= 2 &&
      (sp[0][0] !== sp[sp.length - 1][0] || sp[0][1] !== sp[sp.length - 1][1]),
  );
  const closed = subpaths.filter(
    (sp) =>
      sp.length >= 2 &&
      sp[0][0] === sp[sp.length - 1][0] &&
      sp[0][1] === sp[sp.length - 1][1],
  );
  if (opens.length === 0) {
    return closed;
  }

  const cell = Math.max(epsilon, 0.01);
  const bucketKey = (p: Point): string =>
    `${Math.floor(p[0] / cell)},${Math.floor(p[1] / cell)}`;
  const buckets = new Map<string, Array<{ index: number; end: 0 | 1 }>>();

  const addToBucket = (p: Point, index: number, end: 0 | 1) => {
    const key = bucketKey(p);
    const list = buckets.get(key);
    if (list) {
      list.push({ index, end });
    } else {
      buckets.set(key, [{ index, end }]);
    }
  };

  for (let i = 0; i < opens.length; i += 1) {
    addToBucket(opens[i][0], i, 0);
    addToBucket(opens[i][opens[i].length - 1], i, 1);
  }

  const neighbors = (p: Point): Array<{ index: number; end: 0 | 1 }> => {
    const [kx, ky] = bucketKey(p).split(',').map(Number);
    const out: Array<{ index: number; end: 0 | 1 }> = [];
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) {
        const list = buckets.get(`${kx + dx},${ky + dy}`);
        if (list) {
          out.push(...list);
        }
      }
    }
    return out;
  };

  const used = new Array(opens.length).fill(false);
  const chains: Point[][] = [];
  const epsilon2 = epsilon * epsilon;

  for (let i = 0; i < opens.length; i += 1) {
    if (used[i]) {
      continue;
    }
    used[i] = true;
    let chain = opens[i].map((p) => [p[0], p[1]] as Point);
    let extended = true;
    while (extended) {
      extended = false;
      let best:
        | { distance: number; index: number; mode: string }
        | undefined;

      const tips: Array<{ name: 'tail' | 'head'; tip: Point }> = [
        { name: 'tail', tip: chain[chain.length - 1] },
        { name: 'head', tip: chain[0] },
      ];

      for (const { name, tip } of tips) {
        for (const candidate of neighbors(tip)) {
          if (used[candidate.index]) {
            continue;
          }
          const other = opens[candidate.index];
          const otherPoint =
            candidate.end === 0 ? other[0] : other[other.length - 1];
          const distance = dist2(tip, otherPoint);
          if (distance > epsilon2) {
            continue;
          }
          const mode =
            name === 'tail'
              ? candidate.end === 0
                ? 'tail-head'
                : 'tail-tail'
              : candidate.end === 0
                ? 'head-head'
                : 'head-tail';
          if (!best || distance < best.distance) {
            best = { distance, index: candidate.index, mode };
          }
        }
      }

      if (!best) {
        break;
      }

      const other = opens[best.index];
      used[best.index] = true;
      if (best.mode === 'tail-head') {
        chain = chain.concat(other.slice(1));
      } else if (best.mode === 'tail-tail') {
        chain = chain.concat(other.slice(0, -1).reverse());
      } else if (best.mode === 'head-head') {
        chain = other.slice(1).reverse().concat(chain);
      } else {
        chain = other.slice(0, -1).concat(chain);
      }
      extended = true;
    }
    chains.push(chain);
  }

  return closed.concat(chains);
};

const simplifyPathD = (d: string): string | null => {
  const parsed = parseAbsolutePathD(d);
  if (parsed === null) {
    return null;
  }

  const isOutline = d.length < OUTLINE_D_CHAR_LIMIT;
  const tolerance = isOutline
    ? SIMPLIFY_TOLERANCE * OUTLINE_TOLERANCE_FACTOR
    : SIMPLIFY_TOLERANCE;
  const minSeg = isOutline
    ? MIN_SEGMENT_LENGTH * OUTLINE_MIN_SEG_FACTOR
    : MIN_SEGMENT_LENGTH;

  const filtered = parsed.filter((sp) => {
    if (sp.length === 2 && segmentLength(sp[0], sp[1]) < minSeg) {
      return false;
    }
    return sp.length >= 2;
  });

  const chained = chainSegments(filtered, CHAIN_EPSILON);
  const simplified: Point[][] = [];
  for (const sp of chained) {
    if (polylineLength(sp) < minSeg * 0.5) {
      continue;
    }
    const reduced = rdp(sp, tolerance);
    if (reduced.length >= 2) {
      simplified.push(reduced);
    }
  }

  return pathToD(simplified);
};

type BBox = { minX: number; minY: number; maxX: number; maxY: number };

/** Axis-aligned bounds of absolute M/L path data, or null if unparsable. */
const pathDBounds = (d: string): BBox | null => {
  const parsed = parseAbsolutePathD(d);
  if (!parsed || parsed.length === 0) {
    return null;
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const subpath of parsed) {
    for (const [x, y] of subpath) {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) {
    return null;
  }
  return { minX, minY, maxX, maxY };
};

/** Union of all absolute M/L path bounds inside SVG markup. */
const markupPathBounds = (svg: string): BBox | null => {
  let union: BBox | null = null;
  const re = /\sd="([^"]*)"/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(svg)) !== null) {
    const bounds = pathDBounds(match[1]);
    if (!bounds) {
      continue;
    }
    if (!union) {
      union = { ...bounds };
    } else {
      union.minX = Math.min(union.minX, bounds.minX);
      union.minY = Math.min(union.minY, bounds.minY);
      union.maxX = Math.max(union.maxX, bounds.maxX);
      union.maxY = Math.max(union.maxY, bounds.maxY);
    }
  }
  return union;
};

/**
 * True when simplified bounds stay within {@link BBOX_MAX_RELATIVE_DRIFT} of
 * the original span on every edge and on width/height.
 */
const bboxWithinTolerance = (
  original: BBox,
  simplified: BBox,
  maxRelativeDrift: number = BBOX_MAX_RELATIVE_DRIFT,
): boolean => {
  const spanX = Math.max(original.maxX - original.minX, 1e-6);
  const spanY = Math.max(original.maxY - original.minY, 1e-6);
  const drifts = [
    Math.abs(original.minX - simplified.minX) / spanX,
    Math.abs(original.maxX - simplified.maxX) / spanX,
    Math.abs(original.minY - simplified.minY) / spanY,
    Math.abs(original.maxY - simplified.maxY) / spanY,
    Math.abs(spanX - (simplified.maxX - simplified.minX)) / spanX,
    Math.abs(spanY - (simplified.maxY - simplified.minY)) / spanY,
  ];
  return Math.max(...drifts) <= maxRelativeDrift;
};

/**
 * Simplify path `d` attributes inside SVG markup when the document exceeds
 * `maxBytes`. Returns the original string under the threshold or on failure.
 */
export const maybeSimplifySvgMarkup = (
  svg: string,
  maxBytes: number = SVG_SIMPLIFY_MAX_BYTES,
): string => {
  if (!svg || byteLength(svg) <= maxBytes) {
    return svg;
  }

  const memoHit = simplifyMemo.get(svg);
  if (memoHit !== undefined) {
    return memoHit;
  }

  try {
    const originalBounds = markupPathBounds(svg);
    const next = svg.replace(/\sd="([^"]*)"/g, (match, d: string) => {
      const simplified = simplifyPathD(d);
      if (simplified === null || simplified.length === 0) {
        return match;
      }
      return match.replace(d, simplified);
    });

    // Prefer the smaller result; never grow the payload.
    if (byteLength(next) >= byteLength(svg)) {
      simplifyMemo.set(svg, svg);
      return svg;
    }

    // Reject if the silhouette shifted — leave the original alone rather than
    // permanently writing a wrong footprint back into the plan.
    if (originalBounds) {
      const nextBounds = markupPathBounds(next);
      if (!nextBounds || !bboxWithinTolerance(originalBounds, nextBounds)) {
        simplifyMemo.set(svg, svg);
        return svg;
      }
    }

    simplifyMemo.set(svg, next);
    return next;
  } catch {
    simplifyMemo.set(svg, svg);
    return svg;
  }
};

/**
 * Decode raw SVG markup or a data URL. data: must be checked before the `<svg`
 * sniff — unencoded payloads contain a literal `<svg` and would otherwise be
 * treated as markup, then double-wrapped on re-encode.
 */
const decodeDataUrlSvg = (src: string): string | null => {
  const trimmed = src.trim();
  if (trimmed.startsWith('data:image/svg+xml')) {
    const comma = trimmed.indexOf(',');
    if (comma < 0) {
      return null;
    }
    const payload = trimmed.slice(comma + 1);
    const isBase64 = /;base64/i.test(trimmed.slice(0, comma));
    if (isBase64) {
      try {
        return typeof atob === 'function'
          ? atob(payload)
          : Buffer.from(payload, 'base64').toString('utf8');
      } catch {
        return null;
      }
    }
    try {
      return decodeURIComponent(payload);
    } catch {
      return payload;
    }
  }
  if (trimmed.startsWith('<svg') || trimmed.includes('<svg')) {
    return trimmed;
  }
  return null;
};

const toDataUrl = (svgMarkup: string): string =>
  `data:image/svg+xml;utf8,${encodeURIComponent(svgMarkup)}`;

/**
 * Simplify an svgPath that may be raw markup or a data URL.
 * Webpack /static/media URLs and other non-SVG sources are returned unchanged.
 */
export const maybeSimplifySvgPath = (
  src: string,
  maxBytes: number = SVG_SIMPLIFY_MAX_BYTES,
): { path: string; changed: boolean } => {
  if (!src || typeof src !== 'string') {
    return { path: src, changed: false };
  }

  const memoHit = simplifyMemo.get(src);
  if (memoHit !== undefined) {
    return { path: memoHit, changed: memoHit !== src };
  }

  const decoded = decodeDataUrlSvg(src);
  if (!decoded) {
    simplifyMemo.set(src, src);
    return { path: src, changed: false };
  }

  const simplified = maybeSimplifySvgMarkup(decoded, maxBytes);
  if (simplified === decoded) {
    simplifyMemo.set(src, src);
    return { path: src, changed: false };
  }

  const isDataUrl = src.trim().startsWith('data:image/svg+xml');
  const wasRawMarkup =
    !isDataUrl && (src.trim().startsWith('<') || src.includes('<svg'));
  // Raw markup callers (insert) receive a data URL; data-URL sources stay data URLs.
  const resultPath =
    isDataUrl || wasRawMarkup ? toDataUrl(simplified) : src;
  const changed = resultPath !== src;
  simplifyMemo.set(src, resultPath);
  return { path: resultPath, changed };
};
